import React from "react";
import IndustryPage from "../../components/marketing/IndustryPage";
import { ShoppingBag, Package, CreditCard, TrendingUp, MessageSquare, Sparkles, RefreshCw, Truck } from "lucide-react";

export default function Ecommerce() {
  return (
    <IndustryPage
      slug="ecommerce"
      industry="eCommerce"
      Icon={ShoppingBag}
      accent="#F97316"
      seoTitle="AI Shopping Assistant for Shopify, WooCommerce, BigCommerce & Magento Stores"
      seoDescription="HelloSir.ai is the AI shopping assistant for eCommerce stores. Recommends products from your live catalog, answers shipping & returns questions, recovers abandoned carts and resolves order issues 24/7 — multilingual, on Shopify, WooCommerce, Magento and BigCommerce."
      seoKeywords={[
        "AI shopping assistant", "ecommerce chatbot", "Shopify AI chatbot", "WooCommerce AI assistant",
        "BigCommerce chatbot", "Magento AI", "abandoned cart AI", "product recommendation AI",
        "AI customer support ecommerce", "AI order tracking", "multilingual ecommerce bot",
        "ecommerce voice agent", "AI sales assistant for online store",
      ]}
      hero={{
        headline: "Your store's smartest seller — talks like a person, reads your catalog like a database.",
        sub: "Recommend products from live inventory, answer shipping and return questions, recover abandoned carts, and resolve order issues — all without writing a single rule.",
      }}
      painPoints={[
        { Icon: TrendingUp, title: "Cart abandonment is brutal",  desc: "70% of carts are abandoned. Most shoppers leave with unanswered questions." },
        { Icon: RefreshCw,  title: "Returns & refunds spam inbox", desc: "Repetitive emails about returns drown your support team." },
        { Icon: MessageSquare, title: "Shoppers want a real conversation", desc: "Static FAQ pages never beat a real-time, contextual reply." },
      ]}
      capabilities={[
        { Icon: ShoppingBag, title: "Recommend products",   desc: "Search your live catalog by intent (\"a waterproof hiking jacket under $150\") and reply with images, prices and links." },
        { Icon: Package,     title: "Track orders",          desc: "Looks up an order by email or number and replies with carrier, ETA and tracking URL." },
        { Icon: RefreshCw,   title: "Returns & refunds",     desc: "Issues RMA numbers, prints return labels and explains your policy in the customer's language." },
        { Icon: Truck,       title: "Shipping & delivery",   desc: "Country-aware shipping costs and ETAs read live from your store config." },
        { Icon: CreditCard,  title: "Promo & checkout help", desc: "Applies valid coupons, explains payment options and walks shoppers to checkout." },
        { Icon: Sparkles,    title: "Abandoned-cart recovery", desc: "Triggers a friendly nudge over chat, voice or email when a cart is left behind." },
      ]}
      sampleChats={[
        { q: "I need a gift for my dad — he likes camping and reading. Budget around $80.",
          a: "Two great picks: ① 'TrailBook' camping journal — $42, in stock. ② 'NightOwl' clip-on book light, USB-C — $29. Combined $71. Want me to add both to your cart?" },
        { q: "Where's my order EC-77291?",
          a: "Shipped on Tue 19 Feb via DHL. Currently in transit through Frankfurt. Estimated delivery: Mon 26 Feb. Tracking: dhl.com/track/EC-77291" },
        { q: "Can I return the boots in size 11?",
          a: "Yes — within 30 days of delivery. I've generated return RMA #RM-3382 and emailed you a prepaid label. Refund of $129.00 will hit your card 3–5 days after we receive them." },
      ]}
      sources={[
        "Live Shopify / WooCommerce / Magento / BigCommerce catalog",
        "Order, customer & shipping APIs",
        "Returns and refund policy pages",
        "Promo & coupon database",
      ]}
      integrations={[
        "Shopify", "WooCommerce", "BigCommerce", "Magento", "Stripe", "PayPal",
        "ShipStation", "Klaviyo", "Mailchimp", "Zapier", "Slack", "Custom REST",
      ]}
      metrics={[
        { value: "+22%", label: "Conversion lift on chat-engaged sessions" },
        { value: "−65%", label: "Reduction in 'where's my order' tickets" },
        { value: "24/7", label: "Multilingual support" },
        { value: "10+",  label: "Storefront integrations" },
      ]}
      faqs={[
        { q: "Does it really read my live catalog?",
          a: "Yes. The agent queries your store's API in real time — prices, stock and images are always current. Out-of-stock items are never recommended." },
        { q: "Will it work on Shopify / Woo / Magento / BigCommerce?",
          a: "All four, plus any headless store via REST or GraphQL." },
        { q: "Can it take payments?",
          a: "Not directly — the agent walks shoppers to your secure checkout and pre-applies any valid coupon, so PCI scope stays in Stripe / PayPal." },
        { q: "Does the voice channel work too?",
          a: "Yes. Shoppers can speak to the same agent over the browser voice widget or by calling your phone number — same catalog, same policies." },
      ]}
    />
  );
}
